import { useQuery } from '@tanstack/react-query';
import {
    Accordion,
    Alert,
    Anchor,
    Badge,
    Card,
    Group,
    List,
    Loader,
    Paper,
    SimpleGrid,
    Stack,
    Text,
    Title,
} from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { getLatestOsintReport, type OsintReport, type OsintReportSection } from '../api/osint';
import { safeHttpUrl } from '../utils/url';

/**
 * Display order + titles for the known section types (VGS
 * `OsintReportSectionType`). Unknown types render after these, titled from the
 * raw type, so a new section ai-services adds shows up without a portal deploy.
 */
const SECTION_TITLES: ReadonlyArray<readonly [string, string]> = [
    ['OverallRiskSignal', 'Overall risk signal'],
    ['SummaryAndRiskAssessment', 'Summary & risk assessment'],
    ['VendorIdentity', 'Vendor identity'],
    ['SecurityIncidents', 'Security incidents'],
    ['BreachNotifications', 'Breach notifications'],
    ['ComplianceFrameworksAndCertifications', 'Compliance frameworks & certifications'],
    ['SubProcessors', 'Sub-processors'],
    ['PolicyExistenceChecks', 'Policy existence checks'],
    ['SecurityHygieneSignals', 'Security hygiene signals'],
    ['NewsAndReputationSignals', 'News & reputation signals'],
    ['Sec10KAnalysis', 'SEC 10-K analysis'],
    ['SourcesAndEvidenceLogs', 'Sources & evidence logs'],
    ['ResearchLimitations', 'Research limitations'],
];
const SECTION_ORDER = new Map(SECTION_TITLES.map(([type], index) => [type, index]));
const SECTION_TITLE = new Map(SECTION_TITLES);

const REPORT_STATUS_COLOR: Record<string, string> = {
    COMPLETED: 'green',
    PARTIAL_COMPLETED: 'yellow',
};

const SECTION_STATUS_COLOR: Record<string, string> = {
    SUCCESS: 'green',
    PARTIAL: 'yellow',
    ERROR: 'red',
    PROCESSING: 'blue',
};

/** `primaryDomain` → `Primary domain`. */
const humanize = (key: string): string => {
    const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/_/g, ' ');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};

const formatDate = (iso: string | null): string =>
    iso
        ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
        : '—';

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const isEmpty = (value: unknown): boolean =>
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0) ||
    (isRecord(value) && Object.keys(value).length === 0);

/**
 * Generic renderer for section `content`. The payload shapes are owned by
 * ai-services and versioned (`…V1`), so rather than hand-coding every layout we
 * walk the JSON: objects → labeled rows, primitive arrays → bullet lists,
 * object arrays → one bordered block per item, http(s) strings → links. The
 * `type` discriminator is dropped — it's plumbing, not content.
 */
const OsintValue = ({ value }: { value: unknown }): React.JSX.Element => {
    if (isEmpty(value)) {
        return <Text size="sm">—</Text>;
    }

    if (typeof value === 'string') {
        const href = safeHttpUrl(value);
        return href ? (
            <Anchor href={href} target="_blank" rel="noopener noreferrer" size="sm">
                {value}
            </Anchor>
        ) : (
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                {value}
            </Text>
        );
    }

    if (typeof value === 'boolean') {
        return <Text size="sm">{value ? 'Yes' : 'No'}</Text>;
    }

    if (typeof value === 'number') {
        return <Text size="sm">{String(value)}</Text>;
    }

    if (Array.isArray(value)) {
        if (value.every((item) => !isRecord(item) && !Array.isArray(item))) {
            return (
                <List size="sm" spacing={2}>
                    {value.map((item, index) => (
                        <List.Item key={index}>
                            <OsintValue value={item} />
                        </List.Item>
                    ))}
                </List>
            );
        }
        return (
            <Stack gap="xs">
                {value.map((item, index) => (
                    <Paper key={index} withBorder p="sm" radius="sm">
                        <OsintValue value={item} />
                    </Paper>
                ))}
            </Stack>
        );
    }

    if (isRecord(value)) {
        const entries = Object.entries(value).filter(([key]) => key !== 'type');
        return (
            <Stack gap={6}>
                {entries.map(([key, child]) => (
                    <Group key={key} gap="sm" align="flex-start" wrap="nowrap">
                        <Text size="sm" c="dimmed" w={180} style={{ flexShrink: 0 }}>
                            {humanize(key)}
                        </Text>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <OsintValue value={child} />
                        </div>
                    </Group>
                ))}
            </Stack>
        );
    }

    return <Text size="sm">{String(value)}</Text>;
};

/** Content of the section with this type, when it's an object (the 10-K section can be a bare string). */
const sectionContent = (
    report: OsintReport,
    sectionType: string,
): Record<string, unknown> | undefined => {
    const content = report.sections.find((s) => s.sectionType === sectionType)?.content;
    return isRecord(content) ? content : undefined;
};

/** Strengths / concerns / critical flags from `SummaryAndRiskAssessment`, shown above the sections. */
const RiskHighlights = ({ report }: { report: OsintReport }): React.JSX.Element | null => {
    const summary = sectionContent(report, 'SummaryAndRiskAssessment');
    if (!summary) {
        return null;
    }

    const buckets = [
        { key: 'strengths', label: 'Strengths', color: 'green' },
        { key: 'concerns', label: 'Concerns', color: 'yellow' },
        { key: 'criticalFlags', label: 'Critical flags', color: 'red' },
    ] as const;

    return (
        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
            {buckets.map(({ key, label, color }) => {
                const bucket = summary[key];
                const items =
                    isRecord(bucket) && Array.isArray(bucket.list)
                        ? bucket.list.filter((item): item is string => typeof item === 'string')
                        : [];
                const indicator =
                    isRecord(bucket) && typeof bucket.indicator === 'string'
                        ? bucket.indicator
                        : null;
                return (
                    <Paper key={key} withBorder p="sm" radius="sm">
                        <Group justify="space-between" mb="xs">
                            <Text fw={600} size="sm">
                                {label}
                            </Text>
                            {indicator ? (
                                <Badge color={color} variant="light">
                                    {indicator}
                                </Badge>
                            ) : null}
                        </Group>
                        {items.length > 0 ? (
                            <List size="sm" spacing={2}>
                                {items.map((item, index) => (
                                    <List.Item key={index}>{item}</List.Item>
                                ))}
                            </List>
                        ) : (
                            <Text size="sm" c="dimmed">
                                None reported
                            </Text>
                        )}
                    </Paper>
                );
            })}
        </SimpleGrid>
    );
};

const sortSections = (sections: OsintReportSection[]): OsintReportSection[] =>
    [...sections].sort(
        (a, b) =>
            (SECTION_ORDER.get(a.sectionType) ?? SECTION_TITLES.length) -
            (SECTION_ORDER.get(b.sectionType) ?? SECTION_TITLES.length),
    );

interface OsintReportCardProps {
    /** Numeric candidate id — the same id OSINT reports key on (`catalog_product_id`, F3). */
    catalogProductCandidateId: number;
}

/**
 * OSINT report panel for the Company and Candidate detail views. Shows the
 * vendor's latest finished report (COMPLETED / PARTIAL_COMPLETED): status and
 * dates, the overall risk signal, the strengths / concerns / critical-flags
 * summary, then every section in a collapsible list. Read-only — reports are
 * generated from the Drata app, not from the portal.
 */
export const OsintReportCard = ({
    catalogProductCandidateId,
}: OsintReportCardProps): React.JSX.Element => {
    const {
        data: report,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['osint-report', catalogProductCandidateId],
        queryFn: () => getLatestOsintReport(catalogProductCandidateId),
    });

    const riskSignal = report ? sectionContent(report, 'OverallRiskSignal')?.value : undefined;

    return (
        <Card withBorder radius="md" padding="lg" data-testid="OsintReportCard">
            <Group justify="space-between" mb="md">
                <Title order={4}>OSINT Report</Title>
                {report ? (
                    <Group gap="xs">
                        {typeof riskSignal === 'string' && riskSignal !== '' ? (
                            <Badge variant="outline" data-testid="OsintReportRiskSignal">
                                Risk: {riskSignal}
                            </Badge>
                        ) : null}
                        <Badge
                            color={REPORT_STATUS_COLOR[report.reportStatus] ?? 'gray'}
                            variant="light"
                        >
                            {humanize(report.reportStatus)}
                        </Badge>
                    </Group>
                ) : null}
            </Group>

            {isLoading ? (
                <Loader size="sm" />
            ) : error ? (
                <Alert
                    icon={<IconAlertTriangle size={16} />}
                    color="red"
                    title="Failed to load OSINT report"
                    data-testid="OsintReportError"
                >
                    {error instanceof Error ? error.message : 'Unknown error'}
                </Alert>
            ) : !report ? (
                <Text size="sm" c="dimmed" data-testid="OsintReportEmpty">
                    No OSINT report has been generated for this vendor yet.
                </Text>
            ) : (
                <Stack gap="md">
                    <Group gap="xl">
                        <Text size="sm">
                            <Text span c="dimmed">
                                Report #
                            </Text>
                            {report.id}
                        </Text>
                        <Text size="sm">
                            <Text span c="dimmed">
                                Completed{' '}
                            </Text>
                            {formatDate(report.completedAt)}
                        </Text>
                        <Text size="sm">
                            <Text span c="dimmed">
                                Requested{' '}
                            </Text>
                            {formatDate(report.createdAt)}
                        </Text>
                        <Text size="sm">
                            <Text span c="dimmed">
                                Source{' '}
                            </Text>
                            {humanize(report.source)}
                        </Text>
                    </Group>

                    <RiskHighlights report={report} />

                    {report.sections.length === 0 ? (
                        <Text size="sm" c="dimmed">
                            This report has no sections.
                        </Text>
                    ) : (
                        <Accordion multiple variant="separated" data-testid="OsintReportSections">
                            {sortSections(report.sections).map((section) => (
                                <Accordion.Item
                                    key={section.sectionType}
                                    value={section.sectionType}
                                >
                                    <Accordion.Control>
                                        <Group justify="space-between" pr="sm">
                                            <Text fw={500} size="sm">
                                                {SECTION_TITLE.get(section.sectionType) ??
                                                    humanize(section.sectionType)}
                                            </Text>
                                            <Badge
                                                size="sm"
                                                variant="light"
                                                color={
                                                    SECTION_STATUS_COLOR[
                                                        section.reportSectionStatus
                                                    ] ?? 'gray'
                                                }
                                            >
                                                {humanize(section.reportSectionStatus)}
                                            </Badge>
                                        </Group>
                                    </Accordion.Control>
                                    <Accordion.Panel>
                                        <OsintValue value={section.content} />
                                    </Accordion.Panel>
                                </Accordion.Item>
                            ))}
                        </Accordion>
                    )}
                </Stack>
            )}
        </Card>
    );
};
